<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('Finance') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('finance')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <router-link
                        v-if="!isCustomerFinanceRoute"
                        aria-label="Eintrag anlegen"
                        class="btn-stage-icon-plain"
                        to="/workspace/finance/new"
                        title="Eintrag anlegen"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                    <button
                        v-if="(pagination.total || 0) > 0"
                        :disabled="exporting"
                        :aria-label="exporting ? 'Export läuft' : 'Export'"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="exporting ? 'Export läuft' : 'Export'"
                        @click="exportCsv"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                    </button>
                    <router-link
                        v-if="!isCustomerFinanceRoute"
                        aria-label="Finanzdokumente"
                        class="btn-stage-icon-plain"
                        to="/workspace/finance/documents"
                        title="Finanzdokumente"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.file"></svg-vue>
                    </router-link>
                    <button
                        id="filter-button"
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="openFiltersSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getSales"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex flex-1 min-h-0 overflow-hidden">
            <div class="flex-1 min-w-0 bg-white overflow-auto sm:overflow-hidden">
                <template v-if="displayItems.length > 0">
                    <div class="hidden sm:flex stage-listview-split stage-listview-split-keep-last-border">
                        <div class="stage-listview-split-header">
                            <table class="stage-listview-split-table table-listview">
                                <colgroup>
                                    <col style="width:300px;min-width:300px;">
                                    <col v-if="showRecipientColumn" class="w-[12rem]">
                                    <col class="w-[10rem]">
                                    <col class="w-[10rem]">
                                    <col class="w-[9rem]">
                                    <col style="width:96px;min-width:96px;">
                                    <col :style="statusColumnStyle">
                                </colgroup>
                                <thead>
                                <tr>
                                    <th class="table-listview-l-th w-[18.75rem] min-w-[18.75rem]">{{ $t('Position / Service') }}</th>
                                    <th v-if="showRecipientColumn" class="table-listview-l-th w-[12rem]">Unternehmen</th>
                                    <th class="table-listview-l-th w-[10rem]">{{ amountColumnLabel }}</th>
                                    <th class="table-listview-l-th w-[10rem]">{{ $t('Gross') }}</th>
                                    <th class="table-listview-l-th w-[9rem]">{{ $t('VAT') }}</th>
                                    <th
                                        class="table-listview-l-th"
                                        style="width:96px;min-width:96px;text-align:center;padding-left:0;padding-right:0;"
                                    >
                                        Details
                                    </th>
                                    <th
                                        :class="['table-listview-l-th', {'table-listview-l-th-right': isAdmin}]"
                                        :style="statusColumnStyle"
                                    >
                                        Status
                                    </th>
                                </tr>
                                </thead>
                            </table>
                        </div>
                        <div class="stage-listview-split-body">
                            <table class="stage-listview-split-table table-listview">
                                <colgroup>
                                    <col style="width:300px;min-width:300px;">
                                    <col v-if="showRecipientColumn" class="w-[12rem]">
                                    <col class="w-[10rem]">
                                    <col class="w-[10rem]">
                                    <col class="w-[9rem]">
                                    <col style="width:96px;min-width:96px;">
                                    <col :style="statusColumnStyle">
                                </colgroup>
                                <tbody
                                    v-for="group in displayGroups"
                                    :key="group.key"
                                    :class="group.grouped ? 'stage-finance-group-body' : 'bg-white'"
                                >
                                    <tr
                                        v-if="group.grouped"
                                        class="stage-finance-group-row"
                                    >
                                        <td class="stage-finance-group-cell">
                                            <div class="stage-finance-group-title-content">
                                                <div
                                                    v-if="groupProjectImages(group).length > 0"
                                                    class="flex items-center mr-3 flex-shrink-0"
                                                >
                                                    <img
                                                        v-for="(project, index) in groupProjectImages(group)"
                                                        :key="'workspace-finance-group-project-' + group.key + '-' + project.id"
                                                        :src="project.image_url"
                                                        :alt="project.title || 'Projektbild'"
                                                        :title="project.title || 'Projekt'"
                                                        :class="index !== 0 ? '-ml-6' : ''"
                                                        class="h-8 w-8 rounded-sm object-cover border border-white shadow-solid"
                                                    >
                                                </div>
                                                <span
                                                    v-else-if="group.manual"
                                                    class="stage-finance-entry-icon"
                                                    :class="{'stage-finance-entry-icon--expense': manualFinanceGroupHasExpenses(group)}"
                                                >
                                                    <svg-vue class="h-3.5 w-3.5" icon="font-awesome.solid.euro-sign"></svg-vue>
                                                </span>
                                                <span class="stage-finance-group-copy">
                                                    <span class="stage-finance-group-title-row">
                                                        <span class="stage-finance-group-title" :title="financeGroupTitle(group)">
                                                            {{ financeGroupTitle(group) }}
                                                        </span>
                                                        <identifier-badge
                                                            v-if="!group.manual && group.positionId"
                                                            :value="group.positionId"
                                                            title="Positions-ID"
                                                        ></identifier-badge>
                                                        <span
                                                            v-if="financeGroupIsCompleted(group)"
                                                            class="stage-finance-completion-badge"
                                                            :style="billingStatusBadgeStyle('paid')"
                                                            title="Alle Positionen der Finanzgruppe abgeschlossen"
                                                        >
                                                            <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                                <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.296-7.296a1 1 0 011.408 0z" clip-rule="evenodd" />
                                                            </svg>
                                                        </span>
                                                    </span>
                                                    <span class="stage-finance-group-subline" :title="financeGroupSubline(group)">
                                                        {{ financeGroupSubline(group) }}
                                                    </span>
                                                </span>
                                            </div>
                                        </td>
                                        <td v-if="showRecipientColumn" class="stage-finance-group-empty-cell"></td>
                                        <td class="stage-finance-group-summary-cell">
                                            <span :class="financeGroupAmountClass(group, financeGroupSummaryAmount(group, 'net'))">
                                                {{ formatCurrency(financeGroupSummaryAmount(group, 'net')) }}
                                            </span>
                                        </td>
                                        <td class="stage-finance-group-summary-cell">
                                            <span :class="financeGroupAmountClass(group, financeGroupSummaryAmount(group, 'gross'))">
                                                {{ formatCurrency(financeGroupSummaryAmount(group, 'gross')) }}
                                            </span>
                                        </td>
                                        <td class="stage-finance-group-summary-cell font-medium">
                                            {{ formatGroupVat(group) }}
                                        </td>
                                        <td class="stage-finance-group-empty-cell"></td>
                                        <td class="stage-finance-group-chevron-cell">
                                            <button
                                                type="button"
                                                class="stage-finance-group-toggle"
                                                :aria-expanded="String(!isFinanceGroupCollapsed(group.key))"
                                                :aria-label="isFinanceGroupCollapsed(group.key) ? 'Finanzgruppe öffnen' : 'Finanzgruppe schließen'"
                                                @click="toggleFinanceGroup(group.key)"
                                            >
                                                <svg-vue
                                                    class="stage-finance-group-chevron"
                                                    :class="{'stage-finance-group-chevron--collapsed': isFinanceGroupCollapsed(group.key)}"
                                                    icon="font-awesome.regular.angle-down"
                                                ></svg-vue>
                                            </button>
                                        </td>
                                    </tr>
                                    <template v-if="!group.grouped || !isFinanceGroupCollapsed(group.key)">
                                        <tr
                                            v-for="item in group.items"
                                            :key="item.row_uid"
                                            :class="[
                                                canOpenSettings(item) ? 'cursor-pointer' : '',
                                                group.grouped ? 'stage-finance-group-entry-row' : '',
                                            ]"
                                            @click="openFinanceRow(item)"
                                        >
                                            <td class="table-listview-l-td w-[18.75rem] min-w-[18.75rem] whitespace-no-wrap">
                                                <div class="flex items-center min-w-0">
                                                    <span
                                                        class="stage-finance-entry-icon"
                                                        aria-hidden="true"
                                                    >
                                                        <svg-vue class="h-3.5 w-3.5" icon="font-awesome.solid.euro-sign"></svg-vue>
                                                    </span>
                                                <div class="min-w-0 flex-1">
                                                    <div class="text-sm text-gray-700 font-medium overflow-hidden">
                                                        <div class="flex items-center gap-2 min-w-0">
                                                            <span
                                                                class="block truncate text-gray-700"
                                                                :title="financeGroupEntryTitle(item, group)"
                                                            >
                                                                {{ financeGroupEntryTitle(item, group) }}
                                                            </span>
                                                            <span
                                                                v-if="positionCompletionIsCompleted(item)"
                                                                class="stage-finance-completion-badge"
                                                                :style="financeSourceBadgeStyle(item)"
                                                                :title="financeSourceBadgeTooltip(item)"
                                                            >
                                                                <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                                    <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.296-7.296a1 1 0 011.408 0z" clip-rule="evenodd" />
                                                                </svg>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div class="mt-0.5 text-xs text-gray-500 truncate" :title="approvalDateLabel(item)">
                                                        {{ approvalDateLabel(item) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td v-if="showRecipientColumn" class="table-listview-l-td whitespace-no-wrap w-[12rem]">
                                            <div class="flex items-start min-w-0">
                                                <img
                                                    v-if="rowRecipientCompanyAvatar(item)"
                                                    :src="rowRecipientCompanyAvatar(item)"
                                                    :alt="rowRecipientCompanyLabel(item)"
                                                    :title="rowRecipientCompanyLabel(item)"
                                                    class="h-8 w-8 rounded-sm object-cover border border-white shadow-solid flex-shrink-0 mt-0.5"
                                                >
                                                <span
                                                    v-else
                                                    class="inline-flex items-center justify-center h-8 w-8 rounded-sm border border-bordergray-100 bg-gray-50 text-[11px] font-semibold text-gray-500 flex-shrink-0 mt-0.5"
                                                    :title="rowRecipientCompanyLabel(item)"
                                                >
                                                    {{ rowRecipientCompanyInitial(item) }}
                                                </span>
                                                <div class="ml-2 min-w-0 flex-1">
                                                    <div class="block truncate text-sm text-gray-700 font-medium" :title="rowRecipientCompanyLabel(item)">
                                                        {{ rowRecipientCompanyLabel(item) }}
                                                    </div>
                                                    <div class="block truncate text-xs text-gray-500" :title="rowRecipientInvoiceLabel(item)">
                                                        {{ rowRecipientInvoiceLabel(item) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 w-[10rem]">
                                            <div :class="netAmountClass(item)">{{ formatAmountPrimary(item) }}</div>
                                            <div v-if="showAmountSecondary(item)" class="text-xs text-gray-500">
                                                {{ formatAmountSecondary(item) }}
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 w-[10rem]">
                                            <div :class="grossAmountClass(item)">{{ formatGrossPrimary(item) }}</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap text-sm font-medium text-gray-700 w-[9rem]">
                                            {{ formatVatWithRate(item) }}
                                        </td>
                                        <td
                                            class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 text-center"
                                            style="width:96px;min-width:96px;padding-left:0;padding-right:0;"
                                        >
                                            <div class="flex items-center justify-center w-full">
                                                <app-tooltip
                                                    placement="bottom"
                                                    width-class="w-max max-w-xs"
                                                    class="inline-flex items-center justify-center"
                                                >
                                                    <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.regular.circle-info"></svg-vue>
                                                    <template #content>
                                                        <div class="inline-flex flex-col text-left">
                                                            <div
                                                                v-for="(line, lineIndex) in dateInfoTooltipLines(item)"
                                                                :key="'finance-dates-tooltip-' + item.row_uid + '-' + lineIndex"
                                                            >
                                                                {{ line }}
                                                            </div>
                                                        </div>
                                                    </template>
                                                </app-tooltip>
                                            </div>
                                        </td>
                                        <td
                                            :class="['table-listview-l-td whitespace-no-wrap', {'text-right': isAdmin}]"
                                            :style="statusColumnStyle"
                                        >
                                            <div :class="['inline-flex items-center gap-2', {'justify-end w-full flex-wrap': isAdmin}]">
                                                <span
                                                    :style="billingStatusBadgeStyle(rowBillingStatus(item))"
                                                    class="inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium"
                                                >
                                                    {{ billingStatusLabel(rowBillingStatus(item)) }}
                                                </span>
                                                <span
                                                    v-if="isStornoEntry(item) && rowBillingStatus(item) !== 'reversed'"
                                                    class="inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium bg-red-50 text-red-700 border-red-200"
                                                >
                                                    {{ $t('Reversed') }}
                                                </span>
                                            </div>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>

                <template v-else-if="!loading">
                    <empty-state />
                </template>

            </div>

                </div>
                <div v-if="displayItems.length > 0" class="stage-finance-summary-footer">
                    <div class="stage-finance-summary-grid">
                        <div class="stage-finance-summary-item stage-finance-summary-item--label text-sm leading-6 inline-flex items-center whitespace-nowrap min-w-max text-gray-700">
                            <svg
                                class="h-4 w-4 mr-2 text-gray-500 flex-shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect x="5" y="3" width="14" height="18" rx="2" stroke="currentColor" stroke-width="1.6"/>
                                <rect x="8" y="6" width="8" height="3" rx="0.5" stroke="currentColor" stroke-width="1.4"/>
                                <path d="M8 12H10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M12 12H14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M8 15H10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M12 15H14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M8 18H10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M12 18H16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                            </svg>
                            <span class="font-semibold">{{ summaryLabel }}</span>
                        </div>
                        <div class="stage-finance-summary-item stage-finance-summary-item--value text-sm leading-6 whitespace-nowrap min-w-max text-gray-700">
                            <span :class="summaryAmountClass(summaryNetAmount)">{{ formatCurrency(summaryNetAmount) }}</span>
                            <span class="ml-1 text-gray-500">(Netto)</span>
                        </div>
                        <div class="stage-finance-summary-item stage-finance-summary-item--value text-sm leading-6 whitespace-nowrap min-w-max text-gray-700">
                            <span :class="summaryAmountClass(summaryGrossAmount)">{{ formatCurrency(summaryGrossAmount) }}</span>
                            <span class="ml-1 text-gray-500">({{ $t('Gross') }})</span>
                        </div>
                        <div class="stage-finance-summary-item stage-finance-summary-item--value text-sm leading-6 whitespace-nowrap min-w-max text-gray-700">
                            <span class="font-medium">{{ formatCurrency(summaryVatAmount) }}</span>
                            <span class="ml-1 text-gray-500">({{ $t('VAT') }})</span>
                        </div>
                    </div>
                </div>
                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="displayedTotal"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
                <aside
                    :aria-hidden="!filtersSidebar"
                    class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-40"
                    style="border-top-left-radius:0;border-bottom-left-radius:0;"
                    :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                >
                    <div class="stage-filter-body">
                        <div class="stage-filter-title-row">
                            <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                            <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                        </div>

                        <template v-if="!isCustomerFinanceRoute">
                            <div class="stage-filter-label">Darstellung</div>
                            <div class="mb-3">
                                <input-select
                                    id="finance-grouping-mode"
                                    v-model="groupingModeValue"
                                    class="stage-filter-select"
                                    :options="groupingModeOptions"
                                    option-label="name"
                                    @change="onGroupingModeChange"
                                />
                            </div>
                        </template>

                        <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                        <div class="stage-filter-row mb-5">
                            <button
                                class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                type="button"
                                @click="changeSort"
                            >
                                <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                            </button>
                            <div class="stage-filter-sort-select-wrap">
                                <select
                                    v-model="sort.column"
                                    aria-label="Sort by"
                                    class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                    @change="getSales"
                                >
                                    <option value="position_id">Position (ID)</option>
                                    <option value="title">Titel</option>
                                    <option value="amount">Umsatz</option>
                                    <option value="status">Zahlungsstatus</option>
                                </select>
                                <span class="stage-filter-select-icon">
                                    <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                </span>
                            </div>
                        </div>

                        <div class="stage-filter-label">{{ $t('Search') }}</div>
                        <div class="mb-4">
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                </div>
                                <input
                                    v-model.lazy="filters.search"
                                    class="stage-filter-input form-input block pl-10 text-sm"
                                    :placeholder="$t('Search').toString()"
                                    @change="getSales"
                                >
                            </div>
                        </div>
                        <div class="stage-filter-label">Freigabezeitraum</div>
                        <div class="mb-3">
                            <input-select
                                id="finance-approval-year"
                                v-model="approvalYearFilterValue"
                                class="stage-filter-select"
                                :options="approvalYearOptions"
                                option-label="name"
                                @change="onApprovalYearFilterChange"
                            />
                        </div>
                        <template v-if="isAdmin">
                            <div class="stage-filter-label">{{ $t('Finance type') }}</div>
                            <div class="mb-3">
                                <input-select
                                    id="finance-finance-scope"
                                    v-model="financeScopeFilterValue"
                                    class="stage-filter-select"
                                    :options="financeScopeOptions"
                                    option-label="name"
                                    @change="onFinanceScopeFilterChange"
                                />
                            </div>
                        </template>

                        <div class="stage-filter-label">{{ $t('Overview') }}</div>
                        <div class="mb-3">
                            <input-select
                                id="finance-overview"
                                v-model="overviewFilterValue"
                                class="stage-filter-select"
                                :options="overviewOptions"
                                option-label="name"
                                @change="onOverviewFilterChange"
                            />
                        </div>

                        <div class="stage-filter-label">{{ $t('Settlement status') }}</div>
                        <div class="mb-3">
                            <input-select
                                id="finance-billing-status"
                                v-model="billingStatusFilterValue"
                                class="stage-filter-select"
                                :options="billingStatusOptions"
                                option-label="name"
                                @change="onBillingStatusFilterChange"
                            />
                        </div>

                        <div class="stage-filter-label">{{ providerFilterLabel }}</div>
                        <div class="mb-3">
                            <input-select
                                id="finance-provider"
                                v-model="providerFilterValue"
                                class="stage-filter-select"
                                :options="providerFilterOptions"
                                option-label="name"
                                @change="onProviderFilterChange"
                            >
                                <template v-slot:selectedOption="props">
                                    <template v-if="!props || !props.anySelected">
                                        <span class="block truncate">{{ $t('All') }}</span>
                                    </template>
                                    <template v-else>
                                        <span class="block truncate">{{ props.option ? props.option.name : $t('All') }}</span>
                                    </template>
                                </template>
                                <template v-slot:selectOption="props">
                                    <div class="flex items-center space-x-3">
                                        <div v-if="props.option && Number(props.option.id) > 0 && props.option.avatarUrl" class="flex-shrink-0 inline-block">
                                            <img :src="props.option.avatarUrl" alt="Avatar" class="w-5 h-5 rounded-full">
                                        </div>
                                        <div
                                            class="block truncate"
                                            :class="Number(props.option.id) === Number(providerFilterValue) ? 'font-semibold' : 'font-normal'"
                                        >
                                            {{ props.option.name }}
                                        </div>
                                    </div>
                                </template>
                            </input-select>
                        </div>

                        <div class="stage-filter-label">
                            {{ groupByPosition && !isCustomerFinanceRoute ? 'Positionen pro Seite' : $t('Items per page') }}
                        </div>
                        <div class="mb-3">
                            <input-select
                                id="finance-per-page"
                                v-model="perPage"
                                class="stage-filter-select"
                                :options="perPageOptions"
                                option-label="name"
                                @change="onPerPageFilterChange"
                            />
                        </div>
                        <div v-if="hasActiveFilters" class="col-span-3">
                            <button
                                id="finance-filter-reset-btn"
                                type="button"
                                class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                @click="resetFilters"
                            >
                                {{ $t('Reset filters') }}
                            </button>
                        </div>
                    </div>
                </aside>
            </div>
        </div>

        <div v-if="isCustomerFinanceRoute && detailModal" class="app-dialog-root fixed z-50 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="app-dialog-overlay fixed inset-0 z-40 transition-opacity" @click="detailModal = false">
                    <div class="app-dialog-backdrop absolute inset-0"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <div data-dialog-kind="special" class="app-dialog-panel relative z-50 inline-block align-bottom bg-white rounded-sm text-left overflow-hidden shadow-none transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <button
                        class="absolute top-4 right-4 z-10 inline-flex items-center justify-center h-8 w-8 rounded p-1 text-gray-400 hover:text-gray-600"
                        type="button"
                        aria-label="Close"
                        @click="detailModal = false"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.times-light"></svg-vue>
                    </button>
                    <div class="bg-white p-8">
                        <div class="mt-2">
                            <div class="text-xs font-bold uppercase tracking-wider text-gray-600">Netto</div>
                            <div class="mt-1 text-4xl leading-none font-semibold text-gray-900">
                                {{ formatCurrency(detail.customer_total_amount) }}
                            </div>
                            <div class="mt-2 text-sm text-gray-500">
                                MwSt: {{ formatCurrency(detail.customer_vat_amount) }} ({{ Number(detail.vat_rate || 0).toFixed(2) }}%)
                            </div>
                        </div>

                        <div class="mt-4 border border-bordergray-100 rounded bg-white overflow-y-auto max-h-[52vh]">
                            <div v-if="hasDisplayValue(detail.position_id)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Position-ID</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.position_id }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.position_title)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Position</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.position_title }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.service_category_name)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Leistung</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.service_category_name }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_hours)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Aufwände</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatHours(detail.customer_hours) }}</div>
                            </div>
                            <div v-if="hasQuantification(detail)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Quantifizierung</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDetailQuantification(detail) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_total_amount)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Netto</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatCurrency(detail.customer_total_amount) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_vat_amount)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">MwSt</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatCurrency(detail.customer_vat_amount) }} ({{ Number(detail.vat_rate || 0).toFixed(2) }}%)</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_approved_at)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Freigabe</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDate(detail.customer_approved_at) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.billed_at)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Abgerechnet</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDate(detail.billed_at) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.invoice_number)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Rechnungsnummer</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.invoice_number }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.paid_at)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Bezahlt</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDate(detail.paid_at) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.billing_status)" class="px-4 py-3 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Abrechnung</div>
                                <div class="text-sm text-gray-900 text-right">{{ billingStatusLabel(detail.billing_status) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {resolveAccessState} from "@/utils/access-state";
import {UI_COLORS} from '@/utils/ui-colors';
import {
    queryHasKey,
    queryNumber,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: "finance-list",
    metaInfo() {
        return {
            title: this.$t('Finance').toString()
        }
    },
    data() {
        const routeQuery = (this.$route && this.$route.query) ? this.$route.query : {};
        const initialPage = queryNumber(routeQuery, 'list_page', 1);
        const initialPerPage = queryNumber(routeQuery, 'list_per_page', 100, [10, 25, 50, 100]);
        let initialSortColumn = queryString(routeQuery, 'list_sort_column', 'position_id');
        const legacySortColumns = {
            id: 'position_id',
            position: 'title',
            effort: 'amount',
            approved_at: 'position_id',
        };
        initialSortColumn = legacySortColumns[initialSortColumn] || initialSortColumn;
        if (!['position_id', 'title', 'amount', 'status'].includes(initialSortColumn)) {
            initialSortColumn = 'position_id';
        }
        let initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc');
        if (!['asc', 'desc'].includes(initialSortOrder)) {
            initialSortOrder = 'desc';
        }
        let initialBillingStatus = queryString(routeQuery, queryHasKey(routeQuery, 'list_billing_status') ? 'list_billing_status' : 'billing_status', '');
        if (!['open', 'billed', 'paid', 'reversed'].includes(initialBillingStatus)) {
            initialBillingStatus = null;
        }
        const initialApprovalYear = queryNumber(routeQuery, ['list_approval_year', 'approval_year'], null);
        let initialOverview = queryString(routeQuery, 'list_overview', 'all');
        if (!['all', 'mine'].includes(initialOverview)) {
            initialOverview = 'all';
        }
        let initialFinanceScope = queryString(routeQuery, queryHasKey(routeQuery, 'list_finance_scope') ? 'list_finance_scope' : 'finance_scope', 'all');
        if (!['all', 'income', 'expense'].includes(initialFinanceScope)) {
            initialFinanceScope = 'all';
        }
        const isCustomerFinanceRoute = String(this.$route?.path || '').startsWith('/finance');
        const initialGroupingMode = queryString(routeQuery, 'list_grouping', 'grouped');

        return {
            loading: false,
            filtersSidebar: false,
            page: initialPage !== null && initialPage > 0 ? initialPage : 1,
            perPage: initialPerPage !== null ? initialPerPage : 100,
            sort: {
                column: initialSortColumn,
                order: initialSortOrder,
            },
            filters: {
                search: queryString(routeQuery, 'list_search', null),
                provider_id: queryNumber(routeQuery, 'list_provider_id', null),
                billing_status: initialBillingStatus,
                approval_year: initialApprovalYear,
                overview: initialOverview,
                finance_scope: initialFinanceScope,
            },
            pagination: {
                currentPage: 1,
                perPage: 100,
                total: 0,
                totalPages: 1,
            },
            summary: {
                net: 0,
                vat: 0,
                gross: 0,
            },
            overview: {
                all_count: 0,
                my_count: 0,
            },
            financeScopeCounts: {
                all_count: 0,
                income_count: 0,
                expense_count: 0,
            },
            approvalYears: [],
            providers: [],
            items: [],
            groupSummaries: {},
            groupByPosition: !isCustomerFinanceRoute && initialGroupingMode !== 'flat',
            collapsedFinanceGroups: {},
            exporting: false,
            detailModal: false,
            detail: {},
            settlementStatuses: [
                {value: 'open', label: 'Offen', color: UI_COLORS.text.subtle},
                {value: 'billed', label: 'Abgerechnet', color: '#2563EB'},
                {value: 'paid', label: 'Bezahlt', color: '#11A54D'},
                {value: 'reversed', label: 'Storniert', color: '#EF4444'},
            ],
        };
    },
    computed: {
        accessState() {
            return resolveAccessState(this.$store);
        },
        isCustomerFinanceRoute() {
            const path = String(this.$route?.path || '');
            return path.startsWith('/finance');
        },
        isAdmin() {
            return this.accessState.isAdmin;
        },
        showRecipientColumn() {
            return this.isAdmin && !this.isCustomerFinanceRoute;
        },
        isAllScope() {
            return this.isAdmin && this.financeScopeFilterValue === 'all';
        },
        isExpenseScope() {
            return this.isAdmin && this.financeScopeFilterValue === 'expense';
        },
        amountColumnLabel() {
            return 'Netto';
        },
        statusColumnStyle() {
            return this.isAdmin
                ? 'width:144px;min-width:144px;'
                : 'width:192px;min-width:192px;';
        },
        displayedTotal() {
            return Number(this.pagination.total || 0);
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        filteredProviders() {
            if (this.isCustomerFinanceRoute) {
                return this.providers || [];
            }
            if (!this.isAdmin) {
                return this.providers || [];
            }

            const myId = this.$store && this.$store.state && this.$store.state.user
                ? Number(this.$store.state.user.id || 0)
                : 0;
            return (this.providers || []).filter((provider) => Number(provider.id || 0) !== myId);
        },
        displayItems() {
            const sourceItems = this.items || [];
            const activeStatusFilter = this.billingStatusFilterValue;
            if (this.isCustomerFinanceRoute) {
                const rows = sourceItems.map((item) => ({
                    ...item,
                    row_scope: 'income',
                    row_uid: `${item.id}-customer`,
                }));
                if (activeStatusFilter === 'all') {
                    return rows;
                }
                return rows.filter((row) => this.rowBillingStatus(row) === activeStatusFilter);
            }
            if (!this.isAdmin) {
                const rows = sourceItems.map((item) => ({
                    ...item,
                    row_scope: 'income',
                    row_uid: `${item.id}-income`,
                }));
                if (activeStatusFilter === 'all') {
                    return rows;
                }
                return rows.filter((row) => this.rowBillingStatus(row) === activeStatusFilter);
            }

            const rows = sourceItems.reduce((rows, item) => {
                let scopes = [];
                const sourceType = String(item.source_type || '').toLowerCase();
                if (this.financeScopeFilterValue === 'all') {
                    // Reversal entries are a single accounting event and must not be split
                    // into income/expense rows in all-scope view.
                    if (this.isStornoEntry(item)) {
                        scopes = [this.resolveManualScope(item)];
                    } else if (sourceType === 'position_effort_income') {
                        scopes = ['income'];
                    } else if (sourceType === 'position_effort_expense') {
                        scopes = ['expense'];
                    } else {
                        scopes = [this.resolveManualScope(item)];
                    }
                } else if (this.financeScopeFilterValue === 'expense') {
                    scopes = ['expense'];
                } else {
                    scopes = ['income'];
                }

                scopes.forEach((scope, index) => {
                    rows.push({
                        ...item,
                        row_scope: scope,
                        row_uid: `${item.id}-${scope}-${index}`,
                    });
                });
                return rows;
            }, []);

            if (activeStatusFilter === 'all') {
                return rows;
            }
            return rows.filter((row) => this.rowBillingStatus(row) === activeStatusFilter);
        },
        displayGroups() {
            if (!this.groupByPosition || this.isCustomerFinanceRoute) {
                return [{
                    key: 'flat',
                    grouped: false,
                    manual: false,
                    items: this.displayItems,
                }];
            }

            const groups = [];
            const groupIndexes = {};
            this.displayItems.forEach((item) => {
                const positionId = Number(item?.position_id || 0);
                const key = positionId > 0 ? `position:${positionId}` : 'manual';
                if (groupIndexes[key] === undefined) {
                    groupIndexes[key] = groups.length;
                    groups.push({
                        key,
                        grouped: true,
                        manual: positionId < 1,
                        positionId: positionId > 0 ? positionId : null,
                        items: [],
                    });
                }

                groups[groupIndexes[key]].items.push(item);
            });

            return groups;
        },
        groupingModeValue: {
            get() {
                return this.groupByPosition ? 'grouped' : 'flat';
            },
            set(value) {
                this.groupByPosition = value !== 'flat';
            },
        },
        groupingModeOptions() {
            return [
                {id: 'grouped', name: 'Nach Position gruppiert'},
                {id: 'flat', name: 'Flache Liste'},
            ];
        },
        overviewFilterValue: {
            get() {
                return this.filters.overview === 'mine' ? 'mine' : 'all';
            },
            set(value) {
                this.filters.overview = value === 'mine' ? 'mine' : 'all';
            },
        },
        financeScopeFilterValue: {
            get() {
                if (!this.isAdmin) {
                    return 'income';
                }
                if (this.filters.finance_scope === 'expense') {
                    return 'expense';
                }
                if (this.filters.finance_scope === 'income') {
                    return 'income';
                }
                return 'all';
            },
            set(value) {
                if (!this.isAdmin) {
                    return;
                }
                if (value === 'expense') {
                    this.filters.finance_scope = 'expense';
                    return;
                }
                if (value === 'income') {
                    this.filters.finance_scope = 'income';
                    return;
                }
                this.filters.finance_scope = 'all';
            },
        },
        billingStatusFilterValue: {
            get() {
                return this.filters.billing_status || 'all';
            },
            set(value) {
                this.filters.billing_status = value === 'all' ? null : value;
            },
        },
        approvalYearFilterValue: {
            get() {
                return this.filters.approval_year ? Number(this.filters.approval_year) : 0;
            },
            set(value) {
                const approvalYear = Number(value || 0);
                this.filters.approval_year = approvalYear > 0 ? approvalYear : null;
            },
        },
        providerFilterValue: {
            get() {
                return this.filters.provider_id ? Number(this.filters.provider_id) : 0;
            },
            set(value) {
                const providerId = Number(value || 0);
                this.filters.provider_id = providerId > 0 ? providerId : null;
            },
        },
        overviewOptions() {
            if (this.isCustomerFinanceRoute) {
                return [
                    {id: 'all', name: `Alle Rechnungen (${this.overview.all_count || 0})`},
                ];
            }
            let labelSuffix = this.$t('Income').toString();
            if (this.isAdmin && this.isExpenseScope) {
                labelSuffix = this.$t('Expenses').toString();
            } else if (this.isAdmin && this.isAllScope) {
                labelSuffix = this.$t('Entries').toString();
            }
            const options = [
                {id: 'all', name: `${this.$t('All').toString()} ${labelSuffix} (${this.overview.all_count || 0})`},
            ];

            if (!this.isAdmin) {
                options.push({id: 'mine', name: `${this.$t('My income').toString()} (${this.overview.my_count || 0})`});
            }

            return options;
        },
        providerFilterLabel() {
            if (this.isCustomerFinanceRoute) {
                return 'Leistung von';
            }
            if (this.isAdmin && this.isExpenseScope) {
                return this.$t('Expense from').toString();
            }
            if (this.isAdmin && this.isAllScope) {
                return this.$t('Service by').toString();
            }
            return this.$t('Income from').toString();
        },
        financeScopeOptions() {
            return [
                {id: 'all', name: `${this.$t('Income and expenses').toString()} (${this.financeScopeCounts.all_count || 0})`},
                {id: 'income', name: `${this.$t('Income').toString()} (${this.financeScopeCounts.income_count || 0})`},
                {id: 'expense', name: `${this.$t('Expenses').toString()} (${this.financeScopeCounts.expense_count || 0})`},
            ];
        },
        billingStatusOptions() {
            const options = [{id: 'all', name: this.$t('All').toString()}];
            (this.settlementStatuses || []).forEach((status) => {
                options.push({
                    id: status.value,
                    name: status.label,
                });
            });
            return options;
        },
        approvalYearOptions() {
            const options = [{id: 0, name: 'Alle'}];
            (this.approvalYears || []).forEach((year) => {
                options.push({
                    id: Number(year),
                    name: String(year),
                });
            });
            return options;
        },
        providerFilterOptions() {
            const providerOptions = (this.filteredProviders || []).map((provider) => ({
                id: Number(provider.id),
                name: `${provider.name} (${provider.count || 0})`,
                avatarUrl: this.getUserAvatar(provider),
            }));
            return [{id: 0, name: this.$t('All').toString(), avatarUrl: ''}, ...providerOptions];
        },
        perPageOptions() {
            const defaults = [25, 50, 100, 200];
            const current = Number(this.perPage || 0);
            const values = defaults.includes(current) ? defaults : [...defaults, current].sort((a, b) => a - b);
            return values.map((value) => ({id: value, name: value}));
        },
        summaryNetAmount() {
            return Number(this.summary?.net || 0);
        },
        summaryVatAmount() {
            return Number(this.summary?.vat || 0);
        },
        summaryGrossAmount() {
            return Number(this.summary?.gross || 0);
        },
        summaryLabel() {
            if (this.isAdmin && this.isAllScope) {
                return this.$t('Balance').toString();
            }
            if (this.isAdmin && this.isExpenseScope) {
                return this.$t('Total expenses').toString();
            }
            if (this.isAdmin) {
                return this.$t('Total income').toString();
            }

            return this.$t('Total').toString();
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || Number(this.filters.provider_id || 0) > 0
                || (this.filters.billing_status || null) !== null
                || Number(this.filters.approval_year || 0) > 0
                || this.filters.overview !== 'all'
                || (this.isAdmin && this.financeScopeFilterValue !== 'all')
                || this.sort.column !== 'position_id'
                || this.sort.order !== 'desc'
                || Number(this.perPage || 0) !== 100;
        },
    },
    mounted() {
        this.getFilters();
        this.getSales();
    },
    methods: {
        defaultSettlementStatuses() {
            return [
                {value: 'open', label: this.$t('Open').toString(), color: UI_COLORS.text.subtle},
                {value: 'billed', label: this.$t('Billed').toString(), color: '#2563EB'},
                {value: 'paid', label: this.$t('Paid').toString(), color: '#11A54D'},
                {value: 'reversed', label: this.$t('Reversed').toString(), color: '#EF4444'},
            ];
        },
        normalizeHexColor(color, fallback) {
            const normalized = String(color || '').trim().toUpperCase();
            if (/^#[0-9A-F]{6}$/.test(normalized)) {
                if (['#10B981', '#00A944'].includes(normalized)) {
                    return '#11A54D';
                }

                return normalized;
            }

            const normalizedFallback = String(fallback || UI_COLORS.text.subtle).toUpperCase();
            return ['#10B981', '#00A944'].includes(normalizedFallback) ? '#11A54D' : normalizedFallback;
        },
        normalizeSettlementStatuses(statuses) {
            const fallbackMap = this.defaultSettlementStatuses().reduce((carry, item) => {
                carry[item.value] = item;
                return carry;
            }, {});

            const incomingMap = (Array.isArray(statuses) ? statuses : []).reduce((carry, item) => {
                const value = String((item || {}).value || '').toLowerCase();
                if (['open', 'billed', 'paid', 'reversed'].includes(value)) {
                    carry[value] = item || {};
                }
                return carry;
            }, {});

            return ['open', 'billed', 'paid', 'reversed'].map((value) => {
                const incoming = incomingMap[value] || {};
                const fallback = fallbackMap[value];
                return {
                    value,
                    label: String(incoming.label || fallback.label),
                    color: this.normalizeHexColor(incoming.color || fallback.color, fallback.color),
                };
            });
        },
        settlementStatusMeta(status) {
            const normalized = String(status || '').toLowerCase();
            const fallback = this.defaultSettlementStatuses()[0];
            return (this.settlementStatuses || []).find((item) => item.value === normalized) || fallback;
        },
        hexToRgba(hex, alpha) {
            const normalized = this.normalizeHexColor(hex, UI_COLORS.text.subtle).replace('#', '');
            const r = parseInt(normalized.slice(0, 2), 16);
            const g = parseInt(normalized.slice(2, 4), 16);
            const b = parseInt(normalized.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        },
        openFiltersSidebar() {
            this.filtersSidebar = !this.filtersSidebar;
        },
        toggleFinanceGroup(groupKey) {
            this.collapsedFinanceGroups[groupKey] = !this.isFinanceGroupCollapsed(groupKey);
        },
        isFinanceGroupCollapsed(groupKey) {
            return this.collapsedFinanceGroups[groupKey] === true;
        },
        groupProjectImages(group) {
            const projects = [];
            const projectKeys = new Set();
            (group?.items || []).forEach((item) => {
                this.projectImages(item).forEach((project) => {
                    const key = String(project.id || project.image_url || '');
                    if (key !== '' && !projectKeys.has(key)) {
                        projectKeys.add(key);
                        projects.push(project);
                    }
                });
            });

            return projects;
        },
        financeGroupTitle(group) {
            if (group?.manual) {
                return 'Manuelle Finanzeinträge';
            }

            const firstItem = group?.items?.[0] || null;
            return firstItem?.position_title
                || (group?.positionId ? `Position ${group.positionId}` : '-');
        },
        financeGroupSubline(group) {
            return this.financeGroupCountLabel(group);
        },
        financeGroupSummaryAmount(group, amountKey) {
            const summary = this.groupSummaries?.[group?.key] || null;
            return Number(summary?.[amountKey] || 0);
        },
        formatVatRate(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }

            const rate = Number(value);
            if (!Number.isFinite(rate)) {
                return '-';
            }

            return `${rate.toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}%`;
        },
        financeGroupVatRateLabel(group) {
            const rates = [];
            (group?.items || []).forEach((item) => {
                if (item?.vat_rate === null || item?.vat_rate === undefined || item?.vat_rate === '') {
                    return;
                }

                const rate = Number(item.vat_rate);
                if (Number.isFinite(rate) && !rates.some((existingRate) => Math.abs(existingRate - rate) < 0.0001)) {
                    rates.push(rate);
                }
            });

            if (rates.length === 1) {
                return this.formatVatRate(rates[0]);
            }
            if (rates.length > 1) {
                return 'verschiedene';
            }
            return '';
        },
        formatGroupVat(group) {
            const amount = this.formatCurrency(this.financeGroupSummaryAmount(group, 'vat'));
            const rateLabel = this.financeGroupVatRateLabel(group);
            return rateLabel === '' ? amount : `${amount} (${rateLabel})`;
        },
        financeGroupAmountClass(group, amount) {
            if (this.isCustomerFinanceRoute) {
                return 'text-gray-700 font-medium';
            }

            const directions = new Set(
                (group?.items || []).map((item) => this.financeFlowDirection(item))
            );
            if (directions.size === 1 && directions.has('expense')) {
                return 'text-red-600 font-medium';
            }
            if (directions.size === 1 && directions.has('income')) {
                return 'text-green-600 font-medium';
            }

            const numericAmount = Number(amount || 0);
            if (numericAmount < 0) {
                return 'text-red-600 font-medium';
            }
            if (numericAmount > 0) {
                return 'text-green-600 font-medium';
            }

            return 'text-gray-700 font-medium';
        },
        financeGroupCountLabel(group) {
            let incomeCount = 0;
            let expenseCount = 0;
            (group?.items || []).forEach((item) => {
                if (this.financeFlowDirection(item) === 'expense') {
                    expenseCount += 1;
                } else {
                    incomeCount += 1;
                }
            });

            const incomeLabel = incomeCount === 1 ? 'Einnahme' : 'Einnahmen';
            const expenseLabel = expenseCount === 1 ? 'Ausgabe' : 'Ausgaben';
            return `${incomeCount} ${incomeLabel} · ${expenseCount} ${expenseLabel}`;
        },
        manualFinanceGroupHasExpenses(group) {
            return !!group?.manual && (group.items || []).some((item) => {
                const accountingDirection = String(item?.entry_scope || '').toLowerCase();
                return accountingDirection === 'expense'
                    || (accountingDirection === '' && this.resolveManualScope(item) === 'expense');
            });
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.getSales();
            }
        },
        changeSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.getSales();
        },
        getFilters() {
            const self = this;
            axios.get(self.isCustomerFinanceRoute ? 'api/finance/filters' : 'api/workspace/finance/filters', {
                params: {
                    approval_year: self.filters.approval_year,
                    finance_scope: self.isCustomerFinanceRoute
                        ? null
                        : (self.isAdmin ? self.financeScopeFilterValue : null),
                }
            }).then(function (response) {
                self.overview = response.data.overview || self.overview;
                self.financeScopeCounts = response.data.finance_scope_counts || self.financeScopeCounts;
                self.approvalYears = Array.isArray(response.data.approval_years) ? response.data.approval_years : [];
                self.settlementStatuses = self.normalizeSettlementStatuses(response.data.settlement_statuses || self.settlementStatuses);
                if (self.isAdmin && response.data.finance_scope) {
                    self.filters.finance_scope = response.data.finance_scope;
                }
                self.providers = response.data.providers || [];
            });
        },
        getSales() {
            const self = this;
            self.loading = true;
            self.syncFinanceListRouteState();
            axios.get(self.isCustomerFinanceRoute ? 'api/finance' : 'api/workspace/finance', {
                params: {
                    page: self.page,
                    perPage: self.perPage,
                    sort: JSON.stringify(self.sort),
                    search: self.filters.search,
                    provider_id: self.filters.provider_id,
                    approval_year: self.filters.approval_year,
                    settlement_status: self.filters.billing_status,
                    own_only: self.isCustomerFinanceRoute ? null : (self.filters.overview === 'mine' ? 1 : 0),
                    finance_scope: self.isCustomerFinanceRoute
                        ? null
                        : (self.isAdmin ? self.financeScopeFilterValue : null),
                    group_by_position: self.isCustomerFinanceRoute ? null : (self.groupByPosition ? 1 : 0),
                }
            }).then(function (response) {
                self.items = response.data.items || [];
                self.groupSummaries = response.data.group_summaries || {};
                self.pagination = response.data.pagination || self.pagination;
                self.summary = response.data.summary || {
                    net: 0,
                    vat: 0,
                    gross: 0,
                };
                if (self.isAdmin && response.data.finance_scope) {
                    self.filters.finance_scope = response.data.finance_scope;
                }
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        onFinanceScopeFilterChange() {
            this.filters.provider_id = null;
            this.page = 1;
            this.getFilters();
            this.getSales();
        },
        onGroupingModeChange() {
            this.page = 1;
            this.collapsedFinanceGroups = {};
            this.getSales();
        },
        onOverviewFilterChange() {
            this.filters.provider_id = null;
            if (this.filters.overview === 'mine' && this.$store.state.user && this.$store.state.user.id) {
                this.filters.provider_id = Number(this.$store.state.user.id);
            }
            this.page = 1;
            this.getSales();
        },
        onProviderFilterChange() {
            this.filters.overview = 'all';
            this.page = 1;
            this.getSales();
        },
        onBillingStatusFilterChange() {
            this.page = 1;
            this.getSales();
        },
        onApprovalYearFilterChange() {
            this.page = 1;
            this.getSales();
        },
        onPerPageFilterChange() {
            this.page = 1;
            this.getSales();
        },
        resetFilters() {
            this.filters.search = null;
            this.filters.provider_id = null;
            this.filters.billing_status = null;
            this.filters.approval_year = null;
            this.filters.overview = 'all';
            if (this.isAdmin) {
                this.filters.finance_scope = 'all';
            }
            this.sort.column = 'position_id';
            this.sort.order = 'desc';
            this.perPage = 100;
            this.groupByPosition = !this.isCustomerFinanceRoute;
            this.collapsedFinanceGroups = {};
            this.page = 1;
            this.getFilters();
            this.getSales();
        },
        syncFinanceListRouteState() {
            const listRouteKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_provider_id',
                'list_billing_status',
                'list_approval_year',
                'list_overview',
                'list_finance_scope',
                'list_grouping',
                'billing_status',
                'approval_year',
                'finance_scope',
            ];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, listRouteKeys);

            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 100), 100);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'position_id');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'desc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');
            setQueryValue(nextQuery, 'list_provider_id', Number(this.filters.provider_id || 0) > 0 ? Number(this.filters.provider_id) : null);
            setQueryValue(nextQuery, 'list_billing_status', this.filters.billing_status, '');
            setQueryValue(nextQuery, 'list_approval_year', Number(this.filters.approval_year || 0) > 0 ? Number(this.filters.approval_year) : null);
            setQueryValue(nextQuery, 'list_overview', this.filters.overview, 'all');
            if (!this.isCustomerFinanceRoute) {
                setQueryValue(nextQuery, 'list_grouping', this.groupByPosition ? 'grouped' : 'flat', 'grouped');
            }
            if (!this.isCustomerFinanceRoute && this.isAdmin) {
                setQueryValue(nextQuery, 'list_finance_scope', this.financeScopeFilterValue, 'all');
            }

            if (sameRouteQuery(this.$route.query || {}, nextQuery)) {
                return;
            }

            const routeReplace = this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });

            if (routeReplace && typeof routeReplace.catch === 'function') {
                routeReplace.catch(function () {
                });
            }
        },
        exportCsv() {
            const self = this;
            self.exporting = true;
            axios.get(self.isCustomerFinanceRoute ? 'api/finance/export' : 'api/workspace/finance/export', {
                params: {
                    sort: JSON.stringify(self.sort),
                    search: self.filters.search,
                    provider_id: self.filters.provider_id,
                    approval_year: self.filters.approval_year,
                    settlement_status: self.filters.billing_status,
                    own_only: self.isCustomerFinanceRoute ? null : (self.filters.overview === 'mine' ? 1 : 0),
                    finance_scope: self.isCustomerFinanceRoute
                        ? null
                        : (self.isAdmin ? self.financeScopeFilterValue : null),
                },
                responseType: 'blob',
            }).then(function (response) {
                const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8;'});
                const link = document.createElement('a');
                const contentDisposition = response.headers && response.headers['content-disposition']
                    ? String(response.headers['content-disposition'])
                    : '';
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                const filename = match && match[1]
                    ? match[1]
                    : (self.isCustomerFinanceRoute ? 'finanzen_kunde_export.csv' : 'finance_export.csv');
                link.href = window.URL.createObjectURL(blob);
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
                self.exporting = false;
            }).catch(function () {
                self.exporting = false;
            });
        },
        formatHours(value) {
            const numeric = Number(value || 0);
            const fixed = numeric.toFixed(2);
            if (Number.isInteger(numeric)) {
                return String(parseInt(fixed, 10)) + ' h';
            }
            return fixed + ' h';
        },
        formatHoursAdmin(value) {
            return Number(value || 0).toFixed(2) + ' h';
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(Number(value || 0));
        },
        formatHourlyRate(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }
            return this.formatCurrency(value) + ' / h';
        },
        formatQw(value) {
            const numeric = Number(value || 0);
            const normalized = numeric > 1 && numeric <= 100 ? (numeric / 100) : numeric;
            return normalized.toFixed(2);
        },
        formatDate(value) {
            if (!value) {
                return '-';
            }
            return moment(value).format('DD.MM.YYYY');
        },
        normalizeBillingStatusValue(value, fallback = 'open') {
            const normalized = String(value || '').toLowerCase();
            if (['open', 'billed', 'paid', 'reversed'].includes(normalized)) {
                return normalized;
            }
            return fallback;
        },
        dateInfoLines(item) {
            const lines = [];
            const billedDate = this.formatDate(this.rowBilledAt(item));
            const paidDate = this.formatDate(this.rowPaidAt(item));
            const invoiceNumber = this.rowInvoiceNumber(item);

            if (billedDate !== '-') {
                lines.push(`Abgerechnet am: ${billedDate}`);
            }

            if (paidDate !== '-') {
                lines.push(`Bezahlt am: ${paidDate}`);
            }

            if (invoiceNumber !== '') {
                lines.push(`Rechnungsnummer: ${invoiceNumber}`);
            }

            return lines;
        },
        rowBillingStatus(item) {
            if (!item) {
                return 'open';
            }
            if (this.isStornoEntry(item)) {
                return 'reversed';
            }

            return this.normalizeBillingStatusValue(item.billing_status, 'open');
        },
        rowBilledAt(item) {
            if (!item) {
                return null;
            }
            return item.billed_at || null;
        },
        rowPaidAt(item) {
            if (!item) {
                return null;
            }
            return item.paid_at || null;
        },
        rowInvoiceNumber(item) {
            if (!item) {
                return '';
            }
            return String(item.invoice_number || '').trim();
        },
        dateInfoTooltipLines(item) {
            const lines = this.dateInfoLines(item);
            if (lines.length > 0) {
                return lines;
            }
            return ['Abrechnung ausstehend'];
        },
        billingStatusLabel(status) {
            return this.settlementStatusMeta(status).label;
        },
        billingStatusBadgeStyle(status) {
            const meta = this.settlementStatusMeta(status);
            const normalizedColor = this.normalizeHexColor(meta.color, UI_COLORS.text.subtle);
            if (meta.value === 'billed') {
                return {
                    color: 'rgb(255 255 255)',
                    backgroundColor: this.hexToRgba(normalizedColor, 0.78),
                    borderColor: this.hexToRgba(normalizedColor, 0.35),
                };
            }

            if (normalizedColor === '#11A54D') {
                return {
                    color: 'rgb(255 255 255)',
                    backgroundColor: 'rgb(17 165 77 / 75%)',
                    borderColor: 'rgb(3 120 50 / 30%)',
                };
            }

            if (meta.value === 'open') {
                return {
                    color: this.hexToRgba(normalizedColor, 0.95),
                    backgroundColor: '#FFFFFF',
                    borderColor: this.hexToRgba(normalizedColor, 0.28),
                };
            }

            return {
                color: this.hexToRgba(normalizedColor, 0.95),
                backgroundColor: this.hexToRgba(normalizedColor, 0.12),
                borderColor: this.hexToRgba(normalizedColor, 0.28),
            };
        },
        formatEffortPrimary(item) {
            if (item.amount_type === 'fixed') {
                return this.$t('Fixed').toString();
            }
            if (this.isAdmin) {
                if (this.resolveRowScope(item) === 'expense') {
                    return this.formatHoursAdmin(item.internal_hours);
                }
                return this.formatHoursAdmin(item.customer_hours);
            }
            return this.formatHours(item.internal_hours);
        },
        showEffortSecondary() {
            return false;
        },
        formatEffortSecondary(item) {
            if (this.resolveRowScope(item) === 'expense') {
                return '(customer: ' + this.formatHours(item.customer_hours) + ')';
            }
            return '(internal: ' + this.formatHours(item.internal_hours) + ')';
        },
        formatAmountPrimary(item) {
            return this.formatCurrency(this.rowNetAmountValue(item));
        },
        showAmountSecondary() {
            return false;
        },
        formatAmountSecondary(item) {
            if (this.resolveRowScope(item) === 'expense') {
                return '(customer: ' + this.formatCurrency(item.customer_total_amount) + ')';
            }
            return '(internal: ' + this.formatCurrency(item.internal_total_amount) + ')';
        },
        formatVatPrimary(item) {
            return this.formatCurrency(this.rowVatAmountValue(item));
        },
        formatVatWithRate(item) {
            const amount = this.formatVatPrimary(item);
            const rate = this.formatVatRate(item?.vat_rate);
            return rate === '-' ? amount : `${amount} (${rate})`;
        },
        approvalDateLabel(item) {
            return `Freigabe: ${this.formatDate(item?.customer_approved_at)}`;
        },
        formatGrossPrimary(item) {
            return this.formatCurrency(this.rowGrossAmountValue(item));
        },
        financeGroupEntryTitle(item, group) {
            if (group && !group.manual) {
                return item?.service_category_name || '-';
            }

            return item?.position_title || item?.title || '-';
        },
        rowRecipientCompany(item) {
            if (this.resolveRowScope(item) === 'expense') {
                return item?.manual_expense_company || item?.provider_company || null;
            }

            return item?.manual_income_company || item?.customer_company || item?.customer || null;
        },
        rowRecipientCompanyLabel(item) {
            const company = this.rowRecipientCompany(item);
            if (company && company.name) {
                return company.name;
            }

            if (this.resolveRowScope(item) === 'expense') {
                return item?.manual_expense_company_name_snapshot
                    || item?.provider_company_name_snapshot
                    || this.$t('No company assigned').toString();
            }

            return item?.manual_income_company_name_snapshot
                || item?.customer_company_name_snapshot
                || this.$t('No company assigned').toString();
        },
        rowRecipientCompanyAvatar(item) {
            return this.rowRecipientCompany(item)?.avatar || '';
        },
        rowRecipientCompanyInitial(item) {
            const label = String(this.rowRecipientCompanyLabel(item) || '').trim();
            return label !== '' ? label.charAt(0).toUpperCase() : '-';
        },
        rowRecipientInvoiceLabel(item) {
            return this.resolveRowScope(item) === 'expense'
                ? 'Eingangsrechnung'
                : 'Ausgangsrechnung';
        },
        projectImages(item) {
            return Array.isArray(item?.projects)
                ? item.projects.filter((project) => project && project.image_url)
                : [];
        },
        showFinanceSourceBadge(item) {
            return Number(item && item.position_effort_id || 0) > 0;
        },
        financeSourceBadgeStyle(item) {
            if (!this.positionCompletionIsCompleted(item)) {
                return {};
            }

            return this.billingStatusBadgeStyle('paid');
        },
        financeSourceBadgeTooltip(item) {
            if (!this.showFinanceSourceBadge(item)) {
                return '';
            }

            const completion = item && item.position_completion ? item.position_completion : null;
            if (!completion || !completion.applicable) {
                return this.$t('Position').toString();
            }

            if (completion.is_completed) {
                const completedAt = this.formatDate(completion.completed_at);
                if (completedAt !== '-') {
                    return `${this.$t('Position completed on').toString()} ${completedAt}`;
                }
                return this.$t('Position completed').toString();
            }

            return this.$t('Position not completed').toString();
        },
        positionCompletionIsCompleted(item) {
            const completion = item && item.position_completion ? item.position_completion : null;
            return !!(completion && completion.applicable && completion.is_completed);
        },
        financeGroupIsCompleted(group) {
            const positionItems = (group?.items || []).filter((item) => this.showFinanceSourceBadge(item));
            return positionItems.length > 0
                && positionItems.every((item) => this.positionCompletionIsCompleted(item));
        },
        netAmountClass(item) {
            const value = Math.abs(this.rowNetAmountValue(item));
            if (value === 0) {
                return 'text-gray-600 font-medium';
            }

            const direction = this.financeFlowDirection(item);
            if (direction === 'expense') {
                return 'text-red-600 font-medium';
            }

            if (direction === 'income') {
                return 'text-green-600 font-medium';
            }

            return 'text-gray-600 font-medium';
        },
        grossAmountClass(item) {
            const value = Math.abs(this.rowGrossAmountValue(item));
            if (value === 0) {
                return 'text-gray-600 font-medium';
            }

            const direction = this.financeFlowDirection(item);
            if (direction === 'expense') {
                return 'text-red-600 font-medium';
            }

            if (direction === 'income') {
                return 'text-green-600 font-medium';
            }

            return 'text-gray-600 font-medium';
        },
        summaryAmountClass(value) {
            const showDirectionColor = (this.isAdmin && this.isAllScope)
                || (!this.isAdmin && this.accessState.isServiceProvider);
            if (!showDirectionColor) {
                return 'font-medium';
            }

            const numericValue = Number(value || 0);
            if (numericValue < 0) {
                return 'font-medium text-red-600';
            }
            if (numericValue > 0) {
                return 'font-medium text-green-600';
            }

            return 'font-medium';
        },
        isStornoEntry(item) {
            if (!item) {
                return false;
            }
            return !!item.is_negative || String(item.source_type || '').toLowerCase() === 'finance_reversal';
        },
        canOpenSettings(item) {
            if (this.isCustomerFinanceRoute) {
                return true;
            }
            return !this.isStornoEntry(item);
        },
        openFinanceRow(item) {
            if (!this.canOpenSettings(item)) {
                return;
            }

            if (this.isCustomerFinanceRoute) {
                this.openCustomerFinanceDetail(item.id);
                return;
            }
            this.$router.push(this.financeSettingsPath(item.id));
        },
        financeSettingsPath(entryId) {
            return routeWithReturnTo('/workspace/finance/' + entryId + '/settings', this.$route, '/workspace/finance');
        },
        resolveManualScope(item) {
            return Number(item.customer_company_id || 0) > 0 || Number(item.customer_user_id || 0) > 0
                ? 'income'
                : 'expense';
        },
        isCompanyCustomer(item) {
            return Number(item?.customer_company_id || 0) > 0;
        },
        resolveRowScope(item) {
            if (!this.isAdmin) {
                return 'income';
            }
            if (item && (item.row_scope === 'income' || item.row_scope === 'expense')) {
                return item.row_scope;
            }
            const sourceType = String((item && item.source_type) || '').toLowerCase();
            if (sourceType === 'position_effort_income') {
                return 'income';
            }
            if (sourceType === 'position_effort_expense') {
                return 'expense';
            }
            if (this.financeScopeFilterValue === 'expense') {
                return 'expense';
            }
            if (this.financeScopeFilterValue === 'income') {
                return 'income';
            }
            return this.resolveManualScope(item);
        },
        rowNetAmountValue(item) {
            if (this.isCustomerFinanceRoute) {
                return Number(item.customer_total_amount || 0);
            }
            if (!this.isAdmin) {
                return Number(item.internal_total_amount || 0);
            }
            return this.resolveRowScope(item) === 'expense'
                ? Number(item.internal_total_amount || 0)
                : Number(item.customer_total_amount || 0);
        },
        rowVatAmountValue(item) {
            if (this.isCustomerFinanceRoute) {
                return Number(item.customer_vat_amount || 0);
            }
            if (!this.isAdmin) {
                return Number(item.internal_vat_amount || 0);
            }
            return this.resolveRowScope(item) === 'expense'
                ? Number(item.internal_vat_amount || 0)
                : Number(item.customer_vat_amount || 0);
        },
        rowGrossAmountValue(item) {
            if (this.isCustomerFinanceRoute) {
                return Number(item.customer_total_with_vat || 0);
            }
            if (!this.isAdmin) {
                return Number(item.internal_total_with_vat || 0);
            }
            return this.resolveRowScope(item) === 'expense'
                ? Number(item.internal_total_with_vat || 0)
                : Number(item.customer_total_with_vat || 0);
        },
        financeFlowDirection(item) {
            const scope = this.resolveRowScope(item);
            if (!this.isStornoEntry(item)) {
                return scope;
            }
            return scope === 'expense' ? 'income' : 'expense';
        },
        getUserAvatar(user) {
            if (!user) {
                return '';
            }
            return user.avatar && user.avatar !== 'gravatar' ? user.avatar : user.gravatar;
        },
        openCustomerFinanceDetail(entryId) {
            const self = this;
            self.loading = true;
            axios.get('api/finance/' + entryId).then(function (response) {
                self.detail = response.data || {};
                self.detailModal = true;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        hasDisplayValue(value) {
            if (value === null || value === undefined) {
                return false;
            }
            if (typeof value === 'string') {
                return value.trim() !== '';
            }
            return true;
        },
        hasQuantification(item) {
            return Number(item?.quantification_factor || 0) > 0;
        },
        formatDetailQuantification(detail) {
            const factor = Number(detail && detail.quantification_factor);
            if (!Number.isFinite(factor) || factor <= 0) {
                return 'Keine';
            }

            const value = Number(detail && detail.quantification_value);
            const normalizedValue = Number.isFinite(value) ? value : 0.25;
            const baseAmount = Number(detail && detail.customer_base_amount);
            const totalAmount = Number(detail && detail.customer_total_amount);
            const quantificationAmount = Number.isFinite(baseAmount) && Number.isFinite(totalAmount)
                ? Math.round((totalAmount - baseAmount) * 100) / 100
                : 0;
            const quantificationAmountLabel = quantificationAmount > 0
                ? ` (${this.formatCurrency(quantificationAmount)})`
                : '';

            return `QF ${Math.round(factor)} / QW ${normalizedValue.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}${quantificationAmountLabel}`;
        },
    }
}
</script>
